import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import { ErrorBoxDialog } from "components/error";
import ErrorBox from "components/error/ErrorBox";
import { RestrictedTenantRealm, TenantPermission } from "features/tenant";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { createRoute } from "utils/url";
import { useAccessGrant, useRevokeGrant } from "./useAccessGrants";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    width: "100%",
    marginBottom: theme.spacing(3)
  },
  title: {
    color: "#1e1e1e",
    fontSize: "14.4px",
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  label: {
    color: "#464646",
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  input: {
    color: "#464646",
    fontSize: "1rem",
    lineHeight: 1.32,
    wordBreak: "break-word"
  },
  revokeButton: {
    textTransform: "none"
  },
  paper: {
    marginBottom: theme.spacing(2)
  },
  buttonContainer: {
    padding: theme.spacing(0, 2, 2, 2)
  },
  cancelButton: {
    color: "black",
    borderColor: "#bdbdbd",
    textTransform: "none"
  },
  confirmButton: {
    textTransform: "none"
  }
}));

interface AccessGrantDetailsProps {
  grantId: string;
}

const AccessGrantDetails: React.FC<AccessGrantDetailsProps> = ({ grantId }) => {
  const classes = useStyles();
  const history = useHistory();
  const [openConfirm, setOpenConfirm] = useState(false);

  const { data, loading, error: queryError } = useAccessGrant(grantId);
  const [revokeGrant, { error: mutationError, reset: resetMutation }] =
    useRevokeGrant();

  const handleRevokeCompleted = () => {
    history.push(createRoute("/api/access-grants"));
  };

  const handleRevokeError = () => {
    setOpenConfirm(false);
  };

  const grant = data?.grants?.grant;

  const handleRevoke = () => {
    setOpenConfirm(true);
  };

  const handleConfirmRevoke = async () => {
    await revokeGrant({
      variables: { id: grantId },
      onCompleted: handleRevokeCompleted,
      onError: handleRevokeError
    });
    setOpenConfirm(false);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  return (
    <RestrictedTenantRealm
      requiredPermissions={[TenantPermission.ManageAccessGrants]}
    >
      <div>
        {loading && <LinearProgress />}

        {grant && (
          <>
            <Paper elevation={1} className={classes.paper}>
              <Grid container className={classes.container}>
                <Grid item xs={12}>
                  <Typography className={classes.title}>
                    Grant Information
                  </Typography>
                </Grid>

                <Grid container item xs={12} spacing={4}>
                  <Grid item xs={12} sm={8}>
                    <Typography className={classes.label}>Name</Typography>
                    <Typography className={classes.input}>
                      {grant.name}
                    </Typography>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      style={{ marginTop: 16 }}
                    >
                      <Grid item xs={6}>
                        <Typography className={classes.label}>
                          Grant Id
                        </Typography>
                        <Typography className={classes.input}>
                          {grant.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography className={classes.label}>
                          API Key ID
                        </Typography>
                        <Typography className={classes.input}>
                          {grant.apiKeyId}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      style={{ marginTop: 16 }}
                    >
                      <Grid item xs={6}>
                        <Typography className={classes.label}>
                          Created At
                        </Typography>
                        <Typography className={classes.input}>
                          <DateComponent
                            date={new Date(grant.createdAt)}
                            variant={DateVariant.DETAILED}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography className={classes.label}>
                          Created By
                        </Typography>
                        <Typography className={classes.input}>
                          {grant.createdBy.firstName} {grant.createdBy.lastName}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={4}></Grid>
                  <Grid item xs={12} sm={4}></Grid>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                justifyContent="flex-end"
                className={classes.buttonContainer}
              >
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRevoke}
                    className={classes.revokeButton}
                    data-testid="revoke-grant-button"
                  >
                    Revoke Grant
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Dialog
              open={openConfirm}
              onClose={handleCloseConfirm}
              aria-labelledby="revoke-dialog-title"
            >
              <DialogTitle id="revoke-dialog-title">
                Revoke Access Grant?
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to revoke this access grant? This action
                  cannot be undone and will immediately invalidate all
                  associated tokens.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseConfirm}
                  variant="outlined"
                  className={classes.cancelButton}
                  data-testid="cancel-revoke-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmRevoke}
                  variant="contained"
                  color="error"
                  autoFocus
                  className={classes.confirmButton}
                  data-testid="confirm-revoke-button"
                >
                  Revoke
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        {queryError ? <ErrorBox apolloError={queryError} /> : null}
        <ErrorBoxDialog apolloError={mutationError} onClose={resetMutation} />
      </div>
    </RestrictedTenantRealm>
  );
};

export default AccessGrantDetails;
